import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import PortalBasemapsSource from "@arcgis/core/widgets/BasemapGallery/support/PortalBasemapsSource";
import { memo, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ConfigContext } from "../../../../../utils/ConfigContext";
import { getBasemapMinZoom } from "../../../../../utils/helper";
import Section from "../UI/Section/Section";

const BasemapIcon = (props) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 512 512"
    height="18px"
    width="18px"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M256 48C141.124 48 48 141.125 48 256s93.124 208 208 208c114.875 0 208-93.125 208-208S370.875 48 256 48zm-21.549 384.999c-39.464-4.726-75.978-22.392-104.519-50.932C96.258 348.393 77.714 303.622 77.714 256c0-42.87 15.036-83.424 42.601-115.659.71 8.517 2.463 17.648 2.014 24.175-1.64 23.795-3.988 38.687 9.94 58.762 5.426 7.819 6.759 19.028 9.4 28.078 2.583 8.854 12.902 13.498 20.019 18.953 14.359 11.009 28.096 23.805 43.322 33.494 10.049 6.395 16.326 9.576 13.383 21.839-2.367 9.862-3.028 15.937-8.13 24.723-1.557 2.681 5.877 19.918 8.351 22.392 7.498 7.497 14.938 14.375 23.111 21.125 12.671 10.469-1.231 24.072-7.274 39.117zm147.616-50.932c-25.633 25.633-57.699 42.486-92.556 49.081 4.94-12.216 13.736-23.07 21.895-29.362 7.097-5.476 15.986-16.009 19.693-24.352 3.704-8.332 8.611-15.555 13.577-23.217 7.065-10.899-17.419-27.336-25.353-30.781-17.854-7.751-31.294-18.21-47.161-29.375-11.305-7.954-34.257 4.154-47.02-1.417-17.481-7.633-31.883-20.896-47.078-32.339-15.68-11.809-14.922-25.576-14.922-42.997 12.282.453 29.754-3.399 37.908 6.478 2.573 3.117 11.42 17.042 17.342 12.094 4.838-4.043-3.585-20.249-5.212-24.059-5.005-11.715 11.404-16.284 19.803-24.228 10.96-10.364 34.47-26.618 32.612-34.047s-23.524-28.477-36.249-25.193c-1.907.492-18.697 18.097-21.941 20.859.086-5.746.172-11.491.26-17.237.055-3.628-6.768-7.352-6.451-9.692.8-5.914 17.262-16.647 21.357-21.357-2.869-1.793-12.659-10.202-15.622-8.968-7.174 2.99-15.276 5.05-22.45 8.039 0-2.488-.302-4.825-.662-7.133a176.585 176.585 0 0 1 45.31-13.152l14.084 5.66 9.944 11.801 9.924 10.233 8.675 2.795 13.779-12.995L282 87.929V79.59c27.25 3.958 52.984 14.124 75.522 29.8-4.032.361-8.463.954-13.462 1.59-2.065-1.22-4.714-1.774-6.965-2.623 6.531 14.042 13.343 27.89 20.264 41.746 7.393 14.801 23.793 30.677 26.673 46.301 3.394 18.416 1.039 35.144 2.896 56.811 1.788 20.865 23.524 44.572 23.524 44.572s10.037 3.419 18.384 2.228c-7.781 30.783-23.733 59.014-46.769 82.052z"></path>
  </svg>
);

const SectionBasemap = ({ expanded }) => {
  const view = useSelector((state) => state.mapViewReducer.view);
  const activeModule = useSelector((state) => state.activeModule);

  const { config } = useContext(ConfigContext);
  const [basemaps, setBasemaps] = useState([]);
  const { t } = useTranslation("common");

  async function pollForItems(res) {
    const maxPolls = 600;
    const pollInterval = 100;
    let pollCount = 0;

    function getItemsArray() {
      if (!res || !res.items) return [];
      if (Array.isArray(res.items)) return res.items;
      if (typeof res.items.toArray === "function") return res.items.toArray();
      return Array.from(res.items || []);
    }

    return new Promise((resolve) => {
      function poll() {
        const itemsArray = getItemsArray();
        if (itemsArray.length > 0) {
          resolve(itemsArray);
          return;
        }
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        } else {
          resolve([]); // Timeout, return empty array
        }
      }
      poll();
    });
  }

  useEffect(() => {
    if (!view) return;

    const basemapSource = new PortalBasemapsSource({
      query: `title:"${config.basemapGroup}"`,
    });

    basemapSource.load().then(async (res) => {
      const basemaps = [];
      const items = await pollForItems(res.basemaps);

      for (const basemap of items) {
        if (!basemap) continue;

        await basemap.load();

        const id = basemap.portalItem?.id;
        const activeId = view.map.basemap?.portalItem?.id;

        basemaps.push({
          id: basemap.portalItem?.id,
          title: basemap.title,
          thumbnailUrl: basemap.thumbnailUrl,
          isSelected: activeId === id,
          basemap: basemap,
        });
      }

      setBasemaps(basemaps);
    });
  }, [view]);

  useEffect(() => {
    if (basemaps.length === 0) return;

    const handler = reactiveUtils.watch(
      () => view.map.basemap,
      (mapBasemap) => {
        if (mapBasemap && mapBasemap.portalItem) {
          setBasemaps((prevBasemaps) =>
            prevBasemaps.map((item) => ({
              ...item,
              isSelected:
                item.basemap?.portalItem?.id === mapBasemap.portalItem?.id,
            }))
          );
        }
      }
    );

    return () => {
      handler.remove();
    };
  }, [basemaps]);

  const hanldeBasemapChange = useCallback(
    (basemap) => {
      if (!view) return;
      try {
        const id = basemap?.portalItem?.id;
        if (id) {
          const minZoom =
            config.modules[activeModule]?.minZoom ?? config.minZoom;
          view.constraints.minZoom = getBasemapMinZoom(id, minZoom);
        }
      } catch (err) {
        console.log(err);
      }
      view.map.basemap = basemap;
    },
    [view, activeModule, config]
  );

  if (basemaps.length === 0) return null;

  return (
    <Section
      className="onboarding-basemap"
      expanded={expanded}
      options={basemaps.map((item) => ({
        title: item.id
          ? t(`screen.widget.BasemapGallery.${item.id}.title`, item.title)
          : item.title,
        description: t(
          `screen.widget.BasemapGallery.${item.id}.information`,
          ""
        ),
        icon: (
          <img
            src={item.thumbnailUrl}
            alt={item.title}
            style={{
              width: "14px",
              height: "14px",
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
        ),
        onClick: () => {
          hanldeBasemapChange(item.basemap);
        },
        selected: item.isSelected,
      }))}
      title={t("screen.widget.BasemapGallery.name")}
      information={t("screen.widget.BasemapGallery.information")}
      Icon={BasemapIcon}
      widgetName="Basemap"
    />
  );
};

export default memo(SectionBasemap);
